import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useActivityLog } from "../hooks/useActivityLog";
import { ActivityTable } from "../components/ActivityTable";
import { Button, Input, Select } from "@/shared/ui";
import { Calendar } from "lucide-react";
import { getAllAdmins } from "@/shared/db";

export const ActivityPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    activities,
    total,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    updatePagination,
    refetch,
  } = useActivityLog();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [adminList, setAdminList] = useState<
    { id: string; full_name?: string; email?: string }[]
  >([]);

  useEffect(() => {
    getAllAdmins()
      .then((admins) => setAdminList(admins))
      .catch(() => setAdminList([]));
  }, []);

  const handleFilterChange = (key: string, value: string) => {
    updateFilters({ [key]: value === "all" ? undefined : value });
  };

  const handleDateFilter = () => {
    updateFilters({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
  };

  const totalPages = Math.ceil(total / (pagination.pageSize || 50));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-tiber-900">
          {t("admin.activity.title")}
        </h1>
        <p className="text-sm font-light text-tiber-600/60 mt-1">
          {t("admin.activity.subtitle")}
        </p>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Admin Filter */}
          <Select
            value={filters.admin_id || "all"}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange("admin_id", e.target.value)
            }
            options={[
              { value: "all", label: t("admin.activity.filters.allAdmins") },
              ...adminList.map((a) => ({
                value: a.id,
                label: a.full_name || a.email || a.id,
              })),
            ]}
          />

          {/* Action Filter */}
          <Select
            value={filters.action || "all"}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange("action", e.target.value)
            }
            options={[
              { value: "all", label: t("admin.activity.filters.allActions") },
              {
                value: "delete_document",
                label: t("admin.activity.actions.deleteDocument"),
              },
              {
                value: "delete_user",
                label: t("admin.activity.actions.deleteUser"),
              },
              {
                value: "create_admin",
                label: t("admin.activity.actions.createAdmin"),
              },
              {
                value: "update_admin_role",
                label: t("admin.activity.actions.updateAdminRole"),
              },
              {
                value: "deactivate_admin",
                label: t("admin.activity.actions.deactivateAdmin"),
              },
            ]}
          ></Select>

          {/* Entity Type Filter */}
          <Select
            value={filters.entity_type || "all"}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange("entity_type", e.target.value)
            }
            options={[
              { value: "all", label: t("admin.activity.filters.allEntities") },
              { value: "user", label: t("admin.activity.filters.user") },
              {
                value: "document",
                label: t("admin.activity.filters.document"),
              },
              { value: "admin", label: t("admin.activity.filters.admin") },
            ]}
          ></Select>

          {/* Date Range */}
          <div className="flex items-center space-x-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDateFrom(e.target.value)
              }
              placeholder={t("admin.activity.filters.dateFrom")}
              leftIcon={<Calendar className="w-5 h-5" />}
            />
          </div>
        </div>

        {/* Date To and Apply Button */}
        <div className="mt-4 flex items-center space-x-4">
          <div className="flex items-center space-x-2 flex-1">
            <Input
              type="date"
              value={dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDateTo(e.target.value)
              }
              placeholder={t("admin.activity.filters.dateTo")}
              leftIcon={<Calendar className="w-5 h-5" />}
            />
          </div>
          <Button onClick={handleDateFilter} variant="primary" size="sm">
            {t("common.apply")}
          </Button>
          <Button onClick={refetch} variant="secondary" size="sm">
            {t("common.refresh")}
          </Button>
        </div>

        {/* Results count */}
        <div className="mt-4">
          <p className="text-sm font-light text-tiber-600/60">
            {t("admin.activity.showing", { count: activities.length, total })}
          </p>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg border border-romance-200 overflow-hidden"
      >
        {error ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : (
          <>
            <ActivityTable activities={activities} loading={loading} />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-romance-200 flex items-center justify-between">
                <button
                  onClick={() =>
                    updatePagination({ page: (pagination.page || 1) - 1 })
                  }
                  disabled={pagination.page === 1}
                  className="px-4 py-2 text-sm font-medium text-juridico bg-white border border-romance-200 rounded-lg hover:bg-romance-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("common.previous")}
                </button>
                <span className="text-sm text-juridico">
                  {t("common.pageOfPages", {
                    current: pagination.page,
                    total: totalPages,
                  })}
                </span>
                <button
                  onClick={() =>
                    updatePagination({ page: (pagination.page || 1) + 1 })
                  }
                  disabled={pagination.page === totalPages}
                  className="px-4 py-2 text-sm font-medium text-juridico bg-white border border-romance-200 rounded-lg hover:bg-romance-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("common.next")}
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};
